
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface CreatorProfileInfo {
  username: string | null;
  full_name: string | null;
  avatar_url: string | null;
}

export interface CourseWithProfile {
  id: string;
  title: string;
  thumbnail_url: string | null;
  price: number | null;
  published: boolean;
  profiles: CreatorProfileInfo | null;
  [key: string]: any;
}

export const useStudentCourse = (courseId: string | null) => {
  const [courseData, setCourseData] = useState<CourseWithProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCourseData = async () => {
      if (!courseId) {
        setLoading(false);
        return null;
      }
      
      try {
        setLoading(true);
        
        const { data, error } = await supabase
          .from('courses')
          .select(`
            *,
            creator_id,
            profiles!courses_creator_id_fkey(
              username,
              full_name,
              avatar_url
            )
          `)
          .eq('id', courseId)
          .eq('published', true)
          .single();
          
        if (error) {
          console.error("Error fetching course:", error);
          setLoading(false);
          return null;
        }
        
        setCourseData(data as CourseWithProfile);
      } catch (error: any) {
        console.error("Error in course fetch:", error);
        toast.error("Could not load course information");
      } finally {
        setLoading(false);
      }
    };

    fetchCourseData();
  }, [courseId, navigate]);

  const getCourseDisplayInfo = () => {
    if (!courseData) return null;
    
    const creatorProfile = courseData.profiles || null;
    
    const creatorName = creatorProfile?.full_name || creatorProfile?.username || "Instructor";
    const creatorInitials = creatorName
      .split(" ")
      .map((name: string) => name[0])
      .join("")
      .toUpperCase();
      
    const formatPrice = (price: number | null) => {
      if (price === null || price === 0) return "Free";
      return `$${price.toFixed(2)}`;
    };
    
    return {
      title: courseData.title,
      thumbnail: courseData.thumbnail_url,
      creatorName,
      creatorInitials, 
      creatorAvatar: creatorProfile?.avatar_url,
      price: formatPrice(courseData.price)
    };
  };

  return { 
    courseData, 
    loading, 
    getCourseDisplayInfo 
  };
};
